<template>
  <div class="settings-view is-narrow">
    <RouterLink class="app-link settings-back" to="/settings/users">
      <ChevronLeft class="icon" />
      Users
    </RouterLink>
    <PageHeader :title="user?.name ?? 'User'" :subtitle="user?.email" />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!user || (canManageRoles && !roles)">
      <div class="settings-form-card">
        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Display name</div>
            <div class="setting-row-desc">Shown to other members across the workspace.</div>
          </div>
          <div class="setting-control">
            <input v-model="name" class="setting-input" maxlength="100" @keyup.enter="save" />
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Email address</div>
            <div class="setting-row-desc">Used for sign-in and notifications.</div>
          </div>
          <div class="setting-control">
            <input v-model="email" class="setting-input" type="email" @keyup.enter="save" />
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Password</div>
            <div class="setting-row-desc">Set a new password for this member.</div>
          </div>
          <div class="setting-control">
            <label class="control-field">
              <span class="control-field-label">New password</span>
              <input v-model="password" class="setting-input" type="password" autocomplete="new-password" />
            </label>
            <label class="control-field">
              <span class="control-field-label">Confirm</span>
              <input v-model="passwordConfirmation" class="setting-input" type="password" autocomplete="new-password" />
            </label>
            <button type="button" class="btn btn-primary" :disabled="!password" @click="savePassword">Update password</button>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Email status</div>
            <div class="setting-row-desc">Send a verification link so the member can confirm their email.</div>
          </div>
          <div class="setting-control">
            <span :class="emailStatusClass">{{ user?.emailVerified ? 'Verified' : 'Unverified' }}</span>
            <button
              v-if="user && !user.emailVerified"
              type="button"
              class="btn"
              @click="sendVerificationEmail"
            >
              Send verification email
            </button>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Account status</div>
            <div class="setting-row-desc">Banned members lose access to the workspace after you save changes.</div>
          </div>
          <div class="setting-control">
            <ToggleSwitch
              :model-value="!lockedOut"
              @update:model-value="setAccountActive"
            >
              {{ lockedOut ? 'Banned' : 'Active' }}
            </ToggleSwitch>
          </div>
        </div>

        <div class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Global administrator</div>
            <div class="setting-row-desc">Grants full administrative access across the workspace.</div>
          </div>
          <div class="setting-control">
            <ToggleSwitch
              :model-value="isGlobalAdministrator"
              @update:model-value="setGlobalAdministrator"
            >
              {{ isGlobalAdministrator ? 'Administrator' : 'Not an administrator' }}
            </ToggleSwitch>
          </div>
        </div>

        <div v-if="canManageRoles" class="setting-row">
          <div class="setting-row-head">
            <div class="setting-row-label">Roles</div>
            <div class="setting-row-desc">Assign global roles to this member.</div>
          </div>
          <div class="setting-control">
            <ToggleSwitch
              v-for="role in globalRoles"
              :key="role.id"
              :model-value="selectedGlobalRoleIds.has(role.id)"
              @update:model-value="value => setGlobalRole(role.id, value)"
            >
              {{ role.name }}
            </ToggleSwitch>
          </div>
        </div>
        <div class="settings-save-bar">
          <span class="settings-save-hint" :class="{ dirty: pendingCount > 0 }">
            {{ pendingCount > 0 ? `${pendingCount} pending change${pendingCount > 1 ? 's' : ''}` : 'All changes saved' }}
          </span>
          <button type="button" class="btn btn-primary" :disabled="pendingCount === 0" @click="save">Save user</button>
        </div>
      </div>
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { ChevronLeft } from 'lucide-vue-next';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import type { EntityId } from '../api/entityId';
import {
  BuiltInRoleKey,
  CommandsService,
  PermissionKind,
  RoleScope,
  type GlobalRoleAssignmentChange,
  type GetRolesQueryResponse,
  type UserSummary
} from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import ToggleSwitch from '../components/settings/ToggleSwitch.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';
import {
  validateRequiredEmail,
  validateRequiredPassword,
  validateRequiredUserName
} from '../validation/userValidation';

const route = useRoute();
const session = useSessionStore();
const user = ref<UserSummary | null>(null);
const roles = ref<GetRolesQueryResponse | null>(null);
const name = ref('');
const email = ref('');
const lockedOut = ref(false);
const isGlobalAdministrator = ref(false);
const password = ref('');
const passwordConfirmation = ref('');
const message = ref<string | null>(null);
const selectedGlobalRoleIds = reactive<Set<EntityId>>(new Set<EntityId>());
const originalGlobalRoleIds = ref<Set<EntityId>>(new Set<EntityId>());

const canManageRoles = computed(() => session.hasGlobalPermission(PermissionKind.MANAGE_ROLES));
const globalRoles = computed(() => roles.value?.roles.filter(role => role.scope === RoleScope.GLOBAL) ?? []);
const nameDirty = computed(() => {
  const trimmed = name.value.trim();
  return user.value != null && trimmed !== user.value.name;
});
const emailDirty = computed(() => {
  const trimmed = email.value.trim();
  return user.value != null && trimmed !== user.value.email;
});
const emailStatusClass = computed(() => user.value?.emailVerified ? 'status-pill success' : 'status-pill warning');
const lockedOutDirty = computed(() => user.value != null && lockedOut.value !== user.value.lockedOut);
const administratorDirty = computed(() =>
  user.value != null && isGlobalAdministrator.value !== user.value.isGlobalAdministrator);
const roleDirtyCount = computed(() => {
  let count = 0;
  for (const role of globalRoles.value) {
    if (isGlobalRoleChanged(role.id)) {
      count++;
    }
  }

  return count;
});
const pendingCount = computed(() =>
  Number(nameDirty.value)
  + Number(emailDirty.value)
  + Number(lockedOutDirty.value)
  + Number(administratorDirty.value)
  + roleDirtyCount.value);

onMounted(load);
watch(() => route.params.userId, load);

async function load(): Promise<void> {
  try {
    roles.value = session.pageBootstrap?.roles ?? null;
    const response = session.pageBootstrap?.user ?? null;
    if (response == null || String(response.user.id) !== getUserId() || (canManageRoles.value && roles.value == null)) {
      throw new Error('User bootstrap payload was missing.');
    }

    applyUser(response.user);
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load user.');
  }
}

function applyUser(response: UserSummary): void {
  user.value = response;
  name.value = response.name;
  email.value = response.email;
  lockedOut.value = response.lockedOut;
  isGlobalAdministrator.value = response.isGlobalAdministrator;
  applyGlobalRoles(response);
}

async function save(): Promise<void> {
  if (!user.value || pendingCount.value === 0) {
    return;
  }

  const validationMessage = validateUserSave();
  if (validationMessage != null) {
    message.value = validationMessage;
    return;
  }

  try {
    const profileChanges: {
      name?: string;
      email?: string;
      lockedOut?: boolean;
      isGlobalAdministrator?: boolean;
    } = {};

    if (nameDirty.value) {
      profileChanges.name = name.value.trim();
    }

    if (emailDirty.value) {
      profileChanges.email = email.value.trim();
    }

    if (lockedOutDirty.value) {
      profileChanges.lockedOut = lockedOut.value;
    }

    if (administratorDirty.value) {
      profileChanges.isGlobalAdministrator = isGlobalAdministrator.value;
    }

    const globalRoleAssignments: GlobalRoleAssignmentChange[] = [];
    for (const role of globalRoles.value) {
      if (!isGlobalRoleChanged(role.id)) {
        continue;
      }

      globalRoleAssignments.push({ roleId: role.id, assigned: selectedGlobalRoleIds.has(role.id) });
    }

    await CommandsService.commandUpdateUserAdministrationCommand({
      requestBody: {
        id: user.value.id,
        ...profileChanges,
        globalRoleAssignments
      }
    });
    commitUserChanges();
    message.value = 'User saved.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to save user.');
  }
}

async function savePassword(): Promise<void> {
  if (!user.value || !password.value) {
    return;
  }

  if (password.value !== passwordConfirmation.value) {
    message.value = 'Passwords do not match.';
    return;
  }

  const validationMessage = validateRequiredPassword(password.value);
  if (validationMessage != null) {
    message.value = validationMessage;
    return;
  }

  if (await editPassword(password.value)) {
    password.value = '';
    passwordConfirmation.value = '';
  }
}

function setGlobalAdministrator(value: boolean): void {
  isGlobalAdministrator.value = value;
}

function setAccountActive(active: boolean): void {
  lockedOut.value = !active;
}

async function editPassword(newPassword: string): Promise<boolean> {
  if (!user.value) {
    return false;
  }

  try {
    await CommandsService.commandUpdateUserAdministrationCommand({
      requestBody: { id: user.value.id, password: newPassword }
    });
    message.value = 'Password updated.';
    return true;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to update password.');
    return false;
  }
}

function setGlobalRole(roleId: EntityId, assigned: boolean): void {
  if (assigned) {
    selectedGlobalRoleIds.add(roleId);
  } else {
    selectedGlobalRoleIds.delete(roleId);
  }
}

async function sendVerificationEmail(): Promise<void> {
  if (!user.value) {
    return;
  }

  try {
    await CommandsService.commandForceSendUserVerificationEmailCommand({ requestBody: { userId: user.value.id } });
    message.value = 'Verification email sent.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to send verification email.');
  }
}

function applyGlobalRoles(response: UserSummary): void {
  selectedGlobalRoleIds.clear();
  originalGlobalRoleIds.value = new Set<EntityId>();
  for (const role of globalRoles.value) {
    if (!(response.globalRoleNames ?? []).includes(role.name)) {
      continue;
    }

    selectedGlobalRoleIds.add(role.id);
    originalGlobalRoleIds.value.add(role.id);
  }
}

function commitUserChanges(): void {
  if (!user.value) {
    return;
  }

  applyUser({
    ...user.value,
    name: name.value.trim(),
    email: email.value.trim(),
    lockedOut: lockedOut.value,
    isGlobalAdministrator: isGlobalAdministrator.value,
    globalRoleNames: selectedGlobalRoleNames()
  });
}

function selectedGlobalRoleNames(): string[] {
  if (roles.value == null) {
    return user.value?.globalRoleNames ?? [];
  }

  const names = new Set<string>();
  const administratorRole = globalRoles.value.find(role => role.builtInKey === BuiltInRoleKey.ADMINISTRATOR);
  for (const role of globalRoles.value) {
    if (selectedGlobalRoleIds.has(role.id)) {
      names.add(role.name);
    }
  }

  if (administratorRole != null) {
    if (isGlobalAdministrator.value) {
      names.add(administratorRole.name);
    } else {
      names.delete(administratorRole.name);
    }
  }

  return [...names].sort();
}

function isGlobalRoleChanged(roleId: EntityId): boolean {
  return selectedGlobalRoleIds.has(roleId) !== originalGlobalRoleIds.value.has(roleId);
}

function validateUserSave(): string | null {
  if (nameDirty.value) {
    const nameMessage = validateRequiredUserName(name.value);
    if (nameMessage != null) {
      return nameMessage;
    }
  }

  if (emailDirty.value) {
    const emailMessage = validateRequiredEmail(email.value);
    if (emailMessage != null) {
      return emailMessage;
    }
  }

  return null;
}

function getUserId(): EntityId {
  const value = Array.isArray(route.params.userId) ? route.params.userId[0] : route.params.userId;
  if (!value || !/^\d+$/.test(value)) {
    throw new Error('User id is invalid.');
  }

  return value;
}
</script>
